import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { PRODUCT_IMAGE, heroGuarantees, packDescription } from '../data/content';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Hero() {
  return <section className="relative w-full overflow-hidden bg-seyes" data-id="element-107" data-mp="4lyw0o28qiep">
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-4 w-px bg-margin sm:left-8 lg:left-12" data-id="element-108" data-mp="4njng4t9dl8c" />

      <div className="relative mx-auto max-w-6xl px-8 pb-16 pt-4 sm:px-14 sm:pb-20 sm:pt-6 lg:pb-28" data-id="element-109" data-mp="x9un9d91ji43">
        <div className="flex items-center justify-between border-b border-ink/10 pb-4 sm:pb-5" data-id="element-110" data-mp="sai5h6x1118d">
          <span className="font-display text-xl tracking-tight text-ink sm:text-2xl" data-id="element-111" data-mp="nczs94bz7zuk">
            SCIENCES PRÊTES<span className="align-super text-[0.55em]" data-id="element-112" data-mp="3d1sbczku5nu6">™</span>
          </span>
          <span className="text-sm font-medium text-ink/70" data-id="element-113" data-mp="382p4d9lap7jk">Du CP au CM2</span>
        </div>

        <div className="mt-6 flex flex-col sm:mt-12 lg:mt-16 lg:grid lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-12" data-id="element-114" data-mp="3j8ivmv0mo8sl">
          <div className="contents lg:block" data-id="element-115" data-mp="3o7vl7nivvtyv">
            <motion.h1 initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: EASE
          }} className="order-1 font-display text-[2.5rem] leading-[0.98] tracking-tight text-ink sm:text-7xl sm:leading-[0.95] lg:text-[5.4rem]" data-id="element-116" data-mp="3o5j2v997rhla">
              Préparez une année entière de sciences en seulement{' '}
              <span className="relative inline-block whitespace-nowrap italic text-pen" data-id="element-117" data-mp="35aklk1tcayiw">
                5 minutes
                <svg aria-hidden="true" viewBox="0 0 200 14" preserveAspectRatio="none" className="absolute -bottom-1.5 left-0 h-3 w-full sm:-bottom-2.5" data-id="element-118" data-mp="2viy3pmmhy176">
                  <motion.path d="M3 9 C 45 3, 95 2, 135 6 S 185 11, 197 4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke" initial={{
                  pathLength: 0
                }} animate={{
                  pathLength: 1
                }} transition={{
                  duration: 0.3,
                  delay: 0.35,
                  ease: EASE
                }} data-id="element-119" data-mp="2ljgyt7f2jrc9" />
                </svg>
              </span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.08,
            ease: EASE
          }} className="order-4 mt-8 max-w-xl text-lg leading-relaxed text-ink/75 sm:text-xl" data-id="element-120" data-mp="1622idp26vhee">
              Arrêtez d’improviser. Entrez en classe avec vos supports prêts pour expliquer,
              expérimenter et inspirer.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.16,
            ease: EASE
          }} className="order-3 mt-6 lg:mt-10" data-id="element-121" data-mp="3hpsen5c8br21">
              <CtaButton href="#offer" className="w-full sm:w-auto" data-id="element-122" data-mp="26a9atnmqdgx9" />
              <ul className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-1.5 text-[13px] text-ink/70 sm:mt-6 sm:justify-start sm:gap-x-6 sm:text-sm" data-id="element-123" data-mp="267whyfffpm5f">
                {heroGuarantees.map(item => <li key={item} className="flex items-center gap-2" data-id="element-124" data-mp="2g258wyoth71h">
                    <CheckIcon className="h-4 w-4 text-pen" aria-hidden="true" data-id="element-125" data-mp="2kxc5znnkzdhf" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>

          <motion.figure initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3,
          delay: 0.12,
          ease: EASE
        }} className="relative order-2 mx-auto mt-5 w-full max-w-md sm:mt-12 sm:pt-10 lg:mt-0" data-id="element-126" data-mp="2tz20518ukqiw">
            <div aria-hidden="true" className="absolute right-0 top-0 z-10 hidden rotate-[-5deg] items-end gap-1 sm:flex" data-id="element-127" data-mp="49t5s073b8vi">
              <svg viewBox="0 0 60 50" className="h-10 w-12 text-pen" fill="none" data-id="element-128" data-mp="e9ts7v09a53x">
                <path d="M56 6 C 40 4, 18 12, 10 40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-129" data-mp="99xlho06bzko" />
                <path d="M4 32 L10 42 L18 34" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-130" data-mp="3crpijgt639pq" />
              </svg>
              <span className="font-hand text-[1.7rem] leading-[0.9] text-pen" data-id="element-131" data-mp="3mnafv1najlt5">
                6 volumes
                <br data-id="element-132" data-mp="2yxok6o64nopw" />+ 5 bonus
              </span>
            </div>
            <img src={PRODUCT_IMAGE} alt="Coffret SCIENCES PRÊTES : six volumes du CP au CM2 dont le laboratoire, avec guides, fiches et corrigés" className="relative mx-auto h-48 w-auto drop-shadow-[0_24px_28px_rgba(20,32,61,0.22)] sm:h-auto sm:w-full" data-id="element-133" data-mp="2tzwnlgujfefd" />
            <figcaption className="mx-auto mt-8 hidden max-w-sm text-center text-sm leading-relaxed text-ink/70 sm:block" data-id="element-134" data-mp="zm2bnaprz5t5">
              {packDescription}
            </figcaption>
          </motion.figure>

          <p className="order-5 mt-6 text-sm leading-relaxed text-ink/70 sm:hidden" data-id="element-135" data-mp="1s9y608rvglmr">
            {packDescription}
          </p>
        </div>
      </div>
    </section>;
}